import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge'; 
import { Check, X, Sparkles, Users, ShieldCheck, Crown, Star, Zap, ChevronRight, Rocket } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
// import { getTranslation } from '../translations/translations'; // Artık context'ten alıyoruz
import { apiService } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useFox } from '../context/FoxContext';

const PremiumModal = ({ isOpen, onClose }) => {
  const { language, t } = useLanguage(); // t fonksiyonunu context'ten al
  const { toast } = useToast();
  const { triggerFox } = useFox();
  
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('standard'); 
  const [billingCycle, setBillingCycle] = useState('month'); 
  const [signupCount, setSignupCount] = useState(142);

  useEffect(() => {
    if (isOpen) {
        triggerFox('prem_hint_2', { position: 'premium_mode', mood: 'happy' });
        setSignupCount(prev => prev + Math.floor(Math.random() * 5));
    }
  }, [isOpen, triggerFox]);

  const handleUpgrade = async (planType) => {
    setLoading(true);
    try {
      // Simulating API Call
      await new Promise(resolve => setTimeout(resolve, 1500));
      // Real implementation:
      /*
      const response = await apiService.auth.createCheckoutSession(planType, billingCycle);
      if (response.data?.checkoutUrl) {
          window.location.href = response.data.checkoutUrl;
      }
      */
      toast({ title: "Redirecting to Secure Gateway", description: "Ödeme terminaline aktarılıyorsunuz...", className: "bg-emerald-500 text-white" });
    } catch (error) {
      toast({ title: "Terminal Error", description: "Ödeme ağ geçidine ulaşılamıyor.", variant: "destructive" });
    } finally { setLoading(false); }
  };

  const plans = [
    {
      id: 'free',
      titleKey: 'plan_free_title', 
      subtitleKey: 'plan_free_subtitle',
      price: '0 €',
      periodKey: 'period_forever',
      features: ['feat_3_docs', 'feat_basic_archive', 'feat_ads'],
      buttonVariant: 'ghost',
      color: 'slate'
    },
    {
      id: 'standard',
      titleKey: 'plan_standard_title',
      subtitleKey: 'plan_standard_subtitle',
      price: billingCycle === 'month' ? '4,99 €' : '49,90 €',
      periodKey: billingCycle === 'month' ? 'period_month' : 'period_year',
      features: ['feat_unlimited', 'feat_risk_radar', 'feat_templates', 'feat_no_ads'],
      buttonVariant: 'primary',
      popular: true,
      color: 'blue'
    },
    {
      id: 'pro',
      titleKey: 'plan_pro_title',
      subtitleKey: 'plan_pro_subtitle',
      price: billingCycle === 'month' ? '9,99 €' : '99,90 €',
      periodKey: billingCycle === 'month' ? 'period_month' : 'period_year',
      features: ['feat_all_included', 'feat_datev', 'feat_invoice_tool', 'feat_priority'],
      buttonVariant: 'orange',
      color: 'orange'
    }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl w-[95vw] p-0 border-none bg-transparent shadow-none overflow-hidden max-h-[95vh] outline-none">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 100 }}
          className="bg-[#020617] rounded-[3rem] border border-white/10 p-8 md:p-12 relative overflow-hidden text-left shadow-[0_0_100px_rgba(0,0,0,0.8)]"
        >
            {/* Background Atmosphere */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-orange-600/10 blur-[150px] rounded-full pointer-events-none" />
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-[0.03] mix-blend-overlay"></div>

            <button onClick={onClose} className="absolute top-8 right-8 p-3 bg-white/5 hover:bg-white/10 rounded-full text-slate-400 hover:text-white transition-all z-50">
                <X size={20} />
            </button>

            <header className="text-center mb-16 relative z-10">
                <motion.div 
                    initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}
                    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-orange-500/10 to-purple-500/10 border border-white/10 mb-6 backdrop-blur-md"
                >
                    <Crown size={14} className="text-orange-500" />
                    <span className="text-[10px] font-black text-white uppercase tracking-[0.25em]">Elite Access Terminal</span>
                </motion.div>
                
                <motion.h2 
                    initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.3 }}
                    className="text-5xl md:text-7xl font-black text-white tracking-tighter uppercase italic leading-none mb-6"
                >
                    Upgrade Your <br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-300">Financial Power.</span>
                </motion.h2>
                
                <motion.div 
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
                    className="inline-flex items-center gap-3 px-5 py-2 rounded-2xl bg-white/5 border border-white/5 backdrop-blur-md"
                >
                    <div className="flex -space-x-2">
                        {[1,2,3].map(i => <div key={i} className="w-6 h-6 rounded-full bg-slate-700 border-2 border-[#020617]" />)}
                    </div>
                    <span className="text-xs font-bold text-slate-300">
                        Join <span className="text-white">{signupCount}+</span> members optimizing taxes.
                    </span>
                </motion.div>
            </header>

            <div className="flex justify-center mb-16 relative z-10">
                <div className="bg-[#0f172a] p-1.5 rounded-2xl flex items-center border border-white/10 relative shadow-2xl">
                    <button onClick={() => setBillingCycle('month')} className={`px-8 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${billingCycle === 'month' ? 'bg-white text-black shadow-lg' : 'text-slate-500 hover:text-white'}`}>
                        {t('monthly') || 'MONTHLY'}
                    </button>
                    <button onClick={() => setBillingCycle('year')} className={`px-8 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${billingCycle === 'year' ? 'bg-white text-black shadow-lg' : 'text-slate-500 hover:text-white'}`}>
                        {t('yearly') || 'YEARLY'}
                    </button>
                    <div className="absolute -top-3 -right-3 bg-emerald-500 text-black text-[9px] font-black px-2 py-1 rounded-md shadow-lg transform rotate-6 border border-[#020617]">
                        SAVE 20%
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative z-10 px-4 md:px-10 pb-8">
                {plans.map((plan, index) => {
                    const isSelected = selectedPlan === plan.id;
                    return (
                        <motion.div 
                            key={plan.id} 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 + (index * 0.1) }}
                            onClick={() => setSelectedPlan(plan.id)} 
                            className={`relative rounded-[2.5rem] p-8 flex flex-col cursor-pointer transition-all duration-500 group overflow-hidden ${
                                isSelected 
                                ? `bg-[#0F172A] ring-2 ring-orange-500 shadow-[0_20px_50px_-10px_rgba(234,88,12,0.3)] scale-105 z-20` 
                                : 'bg-white/5 border border-white/5 hover:bg-white/10 opacity-70 hover:opacity-100 hover:scale-[1.02]'
                            }`}
                        >
                            {plan.popular && (
                                <div className="absolute top-0 right-0 bg-orange-600 text-white text-[9px] font-black px-4 py-1 rounded-bl-2xl uppercase tracking-widest">
                                    Recommended
                                </div>
                            )}
                            
                            <div className="mb-8">
                                <h3 className={`text-2xl font-black uppercase italic mb-2 ${isSelected ? 'text-white' : 'text-slate-300'}`}>{t(plan.titleKey)}</h3>
                                <p className="text-xs text-slate-500 font-medium leading-relaxed">{t(plan.subtitleKey)}</p>
                            </div>
                            
                            <div className="mb-10">
                                <span className="text-5xl font-black text-white tracking-tighter">{plan.price}</span>
                                <span className="text-xs text-slate-500 ml-2 font-bold uppercase">{t(plan.periodKey)}</span>
                            </div>

                            <ul className="space-y-4 mb-10 flex-1">
                                {plan.features.map((f, i) => (
                                    <li key={i} className="flex items-center gap-3 text-xs font-medium text-slate-300">
                                        <div className={`p-1 rounded-full ${isSelected ? 'bg-emerald-500/20 text-emerald-500' : 'bg-white/10 text-slate-500'}`}>
                                            <Check size={10} strokeWidth={3} />
                                        </div>
                                        {t(f) || f}
                                    </li>
                                ))}
                            </ul>

                            <Button 
                                onClick={(e) => { e.stopPropagation(); plan.id === 'free' ? onClose() : handleUpgrade(plan.id); }} 
                                className={`w-full h-16 rounded-2xl font-black uppercase tracking-[0.2em] text-[10px] transition-all active:scale-95 group/btn ${
                                    plan.id === 'free' 
                                    ? 'bg-white/5 hover:bg-white/10 text-slate-400 border border-white/5' 
                                    : 'bg-orange-600 hover:bg-orange-500 text-white shadow-xl shadow-orange-900/20'
                                }`}
                            >
                                {loading && plan.id !== 'free' ? (
                                    <Zap className="animate-spin" size={18} />
                                ) : (
                                    <span className="flex items-center justify-center gap-2">
                                        {t('select_plan') || 'Select Plan'} {plan.id !== 'free' && <Rocket size={14} className="group-hover/btn:-translate-y-1 transition-transform" />}
                                    </span>
                                )}
                            </Button>
                        </motion.div>
                    );
                })}
            </div>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};

export default PremiumModal;